/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/

import React, { useState, useEffect, useCallback } from "react";
import {
  Grid,
  Stack,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  IconButton,
  Collapse,
} from "@mui/material";
import { H4 } from "components/generals/_Titles";
import FormikForm from "components/layout/FormikForm";
import { Form, FormikProps } from "formik";
import useHttp from "hooks/http.hook";
import {
  PaymentInterface,
  AccountingMovementInterface,
  QueryResultInterface,
  AssetInterface,
  PaymentAssetRelationInterface,
  ExchangeRateInterface,
  HeaderEnum,
} from "medicalsystem-shared";
import { useTranslation } from "react-i18next";
import Button from "components/generals/Button";
import Autocomplete from "components/generals/Autocomplete";
import CurrencyInput from "components/generals/CurrencyInput";
import { EntityPermissions, FormActionType, PermissionsUser } from "types";
import { MediaCategoryEnum, MediaMimeTypesEnum } from "medicalsystem-shared";
import { CoreAPI_URLs } from "constants/http.const";
import Dropzone from "components/generals/MedicalAssetDropzone";
import FilesViewer from "components/generals/MedicalAssetViewer";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ImageIcon from "@mui/icons-material/Image";
import DescriptionIcon from "@mui/icons-material/Description";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DeleteIcon from "@mui/icons-material/Delete";
import WarningIcon from "@mui/icons-material/Warning";
import Card from "components/generals/Card";
import Box from "components/generals/Box";
import { useRef } from "react";
import { PayerTypeEnum, PaymentMethodEnum, CurrencyEnum } from "medicalsystem-shared";
import { paymentFormYUPValidation } from "utils/form.utils";
import { useAppDispatch } from "store/store";
import EventSlice from "store/slices/event.slice";
import PermissionWrapper from "components/generals/PermissionWrapper";
import Input from "components/generals/Input";
import { calculateAccountingTotals } from "utils/accountingMovements.utils";

/**
 * Properties for the PaymentForm component.
 */
interface PaymentFormProps {
  payment?: Partial<PaymentInterface>;
  accountingId: string;
  onCloseAction: () => void;
  action: FormActionType;
  onSuccess?: () => void;
  disableAssets?: boolean;
}

/**
 * Payment form component for recording and managing patient payments.
 * 
 * Features:
 * - Record payments with amount, method, and currency
 * - Link payment to accounting record
 * - Select payer (patient, insurance, or other)
 * - Display accounting movements with balance calculation
 * - Show patient information and total amounts
 * - Calculate amount due and total paid
 * - Support multiple payment methods (cash, check, credit card, etc.)
 * - Multi-currency support with exchange rate integration
 * - Upload payment receipts and supporting documents
 * - File viewer for uploaded payment documents
 * - Collapsible upload section
 * - Automatic balance calculations
 * - Display payment history and running totals
 * 
 * Payment Methods:
 * - Cash, Check, Credit Card, Debit Card, ACH, Wire Transfer, etc.
 * 
 * Currency Support:
 * - USD, EUR, and other currencies
 * - Real-time exchange rate conversion
 * - Displays amounts in both original and converted currency
 * 
 * File Management:
 * - Supports PDF, images, and documents
 * - Drag-and-drop upload via Dropzone
 * - File preview with FilesViewer component
 * - Delete uploaded files
 * - Category: Payment receipts and supporting documents
 * - Automatic file type detection and icon display
 * 
 * Security:
 * - Permission-based access control via PermissionWrapper
 * - Validates payment amounts and dates
 * - Secure file upload with category enforcement
 * - Uses HeaderEnum.RESOURCE_ID for resource identification
 * 
 * @param {PaymentFormProps} props - Form properties
 * @param {PaymentInterface} props.payment - Payment data to edit or empty for new
 * @param {string} props.accountingId - ID of associated accounting record
 * @param {FormActionType} props.action - Form action (NEW, MODIFY, READ_ONLY)
 * @param {Function} props.onCloseAction - Callback when form closes
 * @param {Function} props.onSuccess - Optional callback on successful save
 * @param {boolean} props.disableAssets - Optional flag to disable file uploads
 * 
 * @returns {JSX.Element} Payment form with amount calculation and file upload
 */
const PaymentForm: React.FC<PaymentFormProps> = ({
  payment,
  accountingId,
  action,
  onCloseAction,
  onSuccess,
  disableAssets = false,
}) => {

  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [payerOptions, setPayerOptions] = useState<{ id: string; label: string }[]>([]);
  const [accountingMovements, setAccountingMovements] = useState<AccountingMovementInterface[]>([]);
  const [patientName, setPatientName] = useState("");
  const [patientId, setPatientId] = useState("");
  const [lastAmountDue, setLastAmountDue] = useState(0);
  const [lastTotalAmount, setLastTotalAmount] = useState(0);
  const [totalPaid, setTotalPaid] = useState(0);
  const [uploadedAssets, setUploadedAssets] = useState<AssetInterface[]>([]);
  const [selectedFile, setSelectedFile] = useState<AssetInterface | null>(null);
  const [showUploadSection, setShowUploadSection] = useState(false);
  const [paymentId, setPaymentId] = useState<string>("");
  const [isPaymentAssetEndpointAvailable, setIsPaymentAssetEndpointAvailable] = useState(true);
  const [assetError, setAssetError] = useState<string>("");
  const [currentExchangeRate, setCurrentExchangeRate] = useState<ExchangeRateInterface | null>(
    null
  );
  const dispatch = useAppDispatch();
  const { addErrorMessage } = EventSlice.actions;
  const [initialValues, setInitialValues] = useState<PaymentInterface>({
    payer: { id: "" },
    payerType: PayerTypeEnum.PATIENT,
    paymentMethod: PaymentMethodEnum.CASH,
    posted: 0,
    folio: "",
    currency: CurrencyEnum.MXN,
    exchangeRate: { id: "" },
    equivalentPosted: 0,
  });
  const [, , sendRequest] = useHttp();
  const hasFetchedMovements = useRef(false);

  const currencyOptions = [
    { id: CurrencyEnum.USD, label: t("currency.usd") },
    { id: CurrencyEnum.MXN, label: t("currency.mxn") },
  ];

  const payerTypeOptions = [
    { id: PayerTypeEnum.PATIENT, label: t("payerTypes.patient") },
    { id: PayerTypeEnum.INSURANCE, label: t("payerTypes.insurance") },
    { id: PayerTypeEnum.THIRD_PARTY, label: t("payerTypes.thirdParty") },
  ];

  const paymentMethodOptions = [
    { id: PaymentMethodEnum.CASH, label: t("paymentMethods.cash") },
    { id: PaymentMethodEnum.CREDIT_CARD, label: t("paymentMethods.creditCard") },
    { id: PaymentMethodEnum.INSURANCE, label: t("paymentMethods.insurance") },
  ];

  const shouldShowAssets = !disableAssets && (action === FormActionType.MODIFY || paymentId);

  const convertMXNToUSD = useCallback(
    (amount: number) => {
      if (!currentExchangeRate) return amount;
      const rate = parseFloat(currentExchangeRate.rate.toString());
      return rate > 0 ? amount / rate : amount;
    },
    [currentExchangeRate]
  );

  const getValueInOfficialCurrency = useCallback(
    (amount: number, currency: CurrencyEnum) => {
      if (currency === CurrencyEnum.USD) {
        return amount;
      } else {
        return convertMXNToUSD(amount);
      }
    },
    [convertMXNToUSD]
  );

  const calculateTotalPaidWithCurrent = useCallback(
    (currentPosted: number, currency: CurrencyEnum = CurrencyEnum.USD) => {
      const currentPaymentInUSD = getValueInOfficialCurrency(currentPosted, currency);
      return currentPaymentInUSD;
    },
    [getValueInOfficialCurrency]
  );

  const calculateRemainingBalance = useCallback(
    (currentPosted: number, currency: CurrencyEnum = CurrencyEnum.USD) => {
      const currentPaymentInUSD = getValueInOfficialCurrency(currentPosted, currency);
      const remainingBalance = lastAmountDue - currentPaymentInUSD;
      return remainingBalance;
    },
    [lastAmountDue, getValueInOfficialCurrency]
  );

  const formatCurrency = (amount: number, currency: CurrencyEnum = CurrencyEnum.USD) => {
    return `$${amount.toFixed(2)} USD`;
  };

  const getPayerLabel = (payerId: string) => {
    const payer = payerOptions.find((option) => option.id === payerId);
    return payer ? payer.label : payerId;
  };

  const getPayerTypeLabel = (payerType: PayerTypeEnum) => {
    const type = payerTypeOptions.find((option) => option.id === payerType);
    return type ? type.label : payerType;
  };

  const getPaymentMethodLabel = (paymentMethod: PaymentMethodEnum) => {
    const method = paymentMethodOptions.find((option) => option.id === paymentMethod);
    return method ? method.label : paymentMethod;
  };

  const getCurrencyLabel = (currency: CurrencyEnum) => {
    const curr = currencyOptions.find((option) => option.id === currency);
    return curr ? curr.label : currency;
  };

  const checkPaymentAssetEndpoint = useCallback(async () => {
    try {
      const response = await fetch(`${CoreAPI_URLs.PAYMENT_ASSET_RELATION}`, {
        method: "HEAD",
      });
      return response.status !== 404;
    } catch (error) {
      console.error("Error checking payment asset endpoint:", error);
      return false;
    }
  }, []);

  useEffect(() => {
    sendRequest<ExchangeRateInterface>(
      CoreAPI_URLs.EXCHANGE_RATE_GENERAL_OPERATIONS,
      (result) => {
        setCurrentExchangeRate(result);
      },
      "GET"
    );
  }, [sendRequest]);

  useEffect(() => {
    if (shouldShowAssets) {
      checkPaymentAssetEndpoint().then(setIsPaymentAssetEndpointAvailable);
    }
  }, [checkPaymentAssetEndpoint, shouldShowAssets]);

  useEffect(() => {
    if (payment && action === FormActionType.MODIFY) {
      const newInitialValues: PaymentInterface = {
        ...payment,
        payer: payment.payer || { id: "" },
        payerType: payment.payerType,
        paymentMethod: payment.paymentMethod ?? PaymentMethodEnum.CASH,
        posted: payment.currency === CurrencyEnum.USD ? payment.posted : payment.equivalentPosted,
        folio: payment.folio ?? "",
        currency: payment.currency ?? CurrencyEnum.MXN,
        exchangeRate: payment.exchangeRate || { id: "" },
        equivalentPosted: payment.equivalentPosted || 0,
      };
      setInitialValues(newInitialValues);
      setPaymentId(payment.id);
    } else {
      const defaultValues: PaymentInterface = {
        payer: { id: "" },
        payerType: PayerTypeEnum.PATIENT,
        paymentMethod: PaymentMethodEnum.CASH,
        posted: 0,
        folio: "",
        currency: CurrencyEnum.MXN,
        exchangeRate: { id: "" },
        equivalentPosted: 0,
      };
      setInitialValues(defaultValues);
      setPaymentId("");
    }
  }, [payment, action]);

  useEffect(() => {
    sendRequest<QueryResultInterface<any>>(
      CoreAPI_URLs.PAYER_GET_ALL,
      (result) => {
        let payersData = [];

        if (result) {
          if (result.results && Array.isArray(result.results)) {
            payersData = result.results;
          } else if (Array.isArray(result)) {
            payersData = result;
          }
        }
        if (payersData.length > 0) {
          const options = payersData.map((item: any) => ({
            id: item.id,
            label: `${item.name || ""} ${item.lastname || ""}`.trim() || item.id,
          }));
          setPayerOptions(options);
        } else {
          setPayerOptions([{ id: "", label: t("paymentForm.noPayersAvailable") }]);
        }
      },
      "GET"
    );
  }, [t, sendRequest]);

  useEffect(() => {
    if (accountingId && !hasFetchedMovements.current) {
      sendRequest<QueryResultInterface<AccountingMovementInterface>>(
        `${CoreAPI_URLs.ACCOUNTING_MOVEMENT_GENERAL_OPERATIONS}/all`,
        (result) => {
          const movementsArray = Array.isArray(result) ? result : result.results || [];
          setAccountingMovements((prev) => {
            if (JSON.stringify(prev) !== JSON.stringify(movementsArray)) {
              const lastMovement = movementsArray?.[0];
              const lastDue = lastMovement?.amountDue ?? 0;
              const totalAmount = lastMovement?.totalAmount ?? 0;
              const { balancePatient } = calculateAccountingTotals(movementsArray);
              setLastAmountDue(balancePatient);
              setLastTotalAmount(totalAmount);
              setTotalPaid(totalAmount - lastDue);
              hasFetchedMovements.current = true;
              setPatientName(
                `${lastMovement?.accounting?.patient?.person?.name || ""} ${lastMovement?.accounting?.patient?.person?.lastname || ""}`.trim()
              );
              return movementsArray;
            }
            return prev;
          });
        },
        "GET",
        undefined,
        [{ header: HeaderEnum.PATIENT_ACCOUNT_NUMBER, value: accountingId }]
      );
    }
  }, [accountingId, action, sendRequest]);

  useEffect(() => {
    if (accountingMovements.length > 0 && accountingMovements[0].accounting?.patient?.person) {
      const person = accountingMovements[0].accounting.patient.person;
      const newPatientName =
        `${typeof person.name === "string" ? person.name : ""} ${typeof person.lastname === "string" ? person.lastname : ""}`.trim();
      if (newPatientName !== patientName) {
        setPatientName(newPatientName);
      }
      const patientFromAccount = accountingMovements[0].accounting.patient;
      if (patientFromAccount?.id && patientFromAccount.id !== patientId) {
        setPatientId(patientFromAccount.id);
      }
    }
  }, [accountingMovements, patientName, patientId]);

  useEffect(() => {
    if (paymentId != "" && shouldShowAssets && isPaymentAssetEndpointAvailable) {
      setAssetError("");
      sendRequest<PaymentAssetRelationInterface[]>(
        CoreAPI_URLs.PAYMENT_ASSET_RELATION,
        (relations) => {
          if (relations && relations.length > 0) {
            const assets = relations
              .map((relation) => relation.asset)
              .filter(Boolean) as AssetInterface[];
            setUploadedAssets(assets);
          } else {
            setUploadedAssets([]);
          }
        },
        "GET",
        undefined,
        [{ header: HeaderEnum.RESOURCE_ID, value: paymentId }]
      );
    } else if (!paymentId || !shouldShowAssets) {
      setUploadedAssets([]);
    }
  }, [paymentId, sendRequest, shouldShowAssets, isPaymentAssetEndpointAvailable, t]);

  const handleDropzoneUpdate = useCallback(() => {
    if (paymentId !== "" && shouldShowAssets && isPaymentAssetEndpointAvailable) {
      setAssetError("");
      sendRequest<PaymentAssetRelationInterface[]>(
        CoreAPI_URLs.PAYMENT_ASSET_RELATION,
        (relations) => {
          if (relations && relations.length > 0) {
            const assets = relations
              .map((relation) => relation.asset)
              .filter(Boolean) as AssetInterface[];
            setUploadedAssets(assets);
          } else {
            setUploadedAssets([]);
          }
        },
        "GET",
        undefined,
        [{ header: HeaderEnum.RESOURCE_ID, value: paymentId }]
      );
    }
  }, [paymentId, sendRequest, shouldShowAssets, isPaymentAssetEndpointAvailable, t]);

  const getFileIcon = (mimeType: string) => {
    switch (mimeType) {
      case MediaMimeTypesEnum.DOCUMENT_PDF:
        return <PictureAsPdfIcon />;
      case MediaMimeTypesEnum.IMAGE_PNG:
      case MediaMimeTypesEnum.IMAGE_JPG:
      case MediaMimeTypesEnum.IMAGE_PJPEG:
        return <ImageIcon />;
      case MediaMimeTypesEnum.DOCUMENT_DOC:
      case MediaMimeTypesEnum.DOCUMENT_DOCX:
        return <DescriptionIcon />;
      default:
        return <InsertDriveFileIcon />;
    }
  };

  const handleRemoveAsset = async (assetId: string) => {
    if (shouldShowAssets && isPaymentAssetEndpointAvailable && paymentId) {
      try {
        await new Promise<void>((resolve, reject) => {
          sendRequest(
            `${CoreAPI_URLs.PAYMENT_ASSET_RELATION}/${assetId}`,
            () => resolve(),
            "DELETE",
            undefined,
            [{ header: HeaderEnum.RESOURCE_ID, value: paymentId }]
          );
        });
      } catch (error) {
        console.error("Error removing asset from backend:", error);
      }
    }
    setUploadedAssets((prev) => prev.filter((asset) => asset.id !== assetId));
    if (selectedFile?.id === assetId) {
      setSelectedFile(null);
    }
  };

  const handleFileClick = (asset: AssetInterface) => {
    setSelectedFile(asset);
  };

  const onSubmit = async (values: PaymentInterface) => {
    if (action === FormActionType.MODIFY) {
      if (onSuccess) {
        onSuccess();
      }
      onCloseAction();
      return;
    }

    setIsLoading(true);
    try {
      const latestExchangeRate: ExchangeRateInterface = await new Promise((resolve, reject) => {
        sendRequest<ExchangeRateInterface>(
          CoreAPI_URLs.EXCHANGE_RATE_GENERAL_OPERATIONS,
          (result) => resolve(result),
          "GET",
          undefined,
          undefined
        );
      });

      const paymentAmountInUSD = getValueInOfficialCurrency(values.posted, values.currency);

      if (values.currency === CurrencyEnum.MXN) {
        const rate = parseFloat(latestExchangeRate.rate.toString());
        if (rate && values.posted > 0) {
          const postedInUSD = values.posted / rate;
          values.equivalentPosted = values.posted;
          values.posted = postedInUSD;
        }
        values.exchangeRate = latestExchangeRate;
      } else if (values.currency === CurrencyEnum.USD) {
        values.exchangeRate = latestExchangeRate;
        values.equivalentPosted = values.posted;
      }

      if (!values.folio) {
        values.folio = `TEMP-${Math.floor(Math.random() * 100000)}`;
      }

      if (lastAmountDue > 0) {
        if (paymentAmountInUSD > lastAmountDue) {
          dispatch(addErrorMessage({ message: t("paymentForm.paymentAmountGreaterThanDebt"), id: crypto.randomUUID() }));
          setIsLoading(false);
        }
        const paymentResult = await new Promise<PaymentInterface>((resolve, reject) => {
          sendRequest<PaymentInterface, void>(
            CoreAPI_URLs.PAYMENT_GENERAL_OPERATIONS,
            (result) => {
              setPaymentId(result.id);
              resolve(result);
            },
            "POST",
            JSON.stringify(values),
            [{ header: HeaderEnum.RESOURCE_ID, value: accountingId }],
            { successMessage: t("paymentForm.paymentCreated") }
          );
        });

        const newPaymentId = paymentResult.id;
        const newAmountDue = lastAmountDue - paymentAmountInUSD;

        const accountMovementPayload: Partial<AccountingMovementInterface> = {
          accounting: { id: accountingId },
          billingEncounter: null,
          payment: { id: newPaymentId },
          date: new Date(),
          dueDate: new Date(),
          amountDue: newAmountDue < 0 ? 0 : newAmountDue,
          totalAmount: lastTotalAmount,
        };

        await new Promise<void>((resolve, reject) => {
          sendRequest<AccountingMovementInterface, void>(
            CoreAPI_URLs.ACCOUNTING_MOVEMENT_GENERAL_OPERATIONS,
            () => resolve(),
            "POST",
            JSON.stringify(accountMovementPayload),
            [{ header: HeaderEnum.PATIENT_ACCOUNT_NUMBER, value: accountingId }],
            { successMessage: t("paymentForm.paymentCreated") }
          );
        });
        if (onSuccess) {
          onSuccess();
        }
      } else {
        dispatch(addErrorMessage({ message: t("paymentForm.patientHasNoDebt"), id: crypto.randomUUID() }));
        setIsLoading(false);
        return;
      }
      onCloseAction();
    } catch (error) {
      console.error("Error creating payment:", error);
      setIsLoading(false);
    }
  };

  return (
    <FormikForm
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={paymentFormYUPValidation(t)}
      enableReinitialize={true}
    >
      {(formikProps: FormikProps<PaymentInterface>) => {
        const remainingBalance = calculateRemainingBalance(
          formikProps?.values?.posted || 0,
          formikProps?.values?.currency
        );
        const isRemainingBalanceNegative = remainingBalance < 0;
        return (
          <Form id="paymentForm" autoComplete="off">
            <Stack alignItems="center" direction="row" gap={2} mb={3}>
              <H4>
                {action === FormActionType.NEW
                  ? t("paymentForm.newPayment")
                  : t("paymentForm.viewPayment")}
              </H4>
            </Stack>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6} id="cy-payeridFormContainer">
                {action === FormActionType.NEW ? (
                  <Autocomplete
                    id="cy-payerid"
                    options={payerOptions}
                    name="payer.id"
                    label={t("paymentForm.payer")}
                    value={formikProps.values.payer?.id || ""}
                    defaultValue={payerOptions.find(
                      (option) => option.id === formikProps.values.payer?.id
                    )}
                    onChange={(event, selectedOption) => {
                      formikProps.setFieldValue("payer.id", selectedOption?.id || "");
                      formikProps.setFieldValue("payer", { id: selectedOption?.id || "" });
                    }}
                    noOptionsText={t("paymentForm.noOptions")}
                  />
                ) : (
                  <Input
                    label={t("paymentForm.payer")}
                    value={getPayerLabel(formikProps.values.payer?.id || "")}
                    disabled
                    fullWidth
                  />
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                <Input
                  label={t("paymentForm.patientName")}
                  value={patientName || t("paymentForm.loading")}
                  disabled
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                {action === FormActionType.NEW ? (
                  <CurrencyInput
                    id="cy-posted"
                    label={t("paymentForm.posted")}
                    value={formikProps.values.posted}
                    onChange={(value) => {
                      formikProps.setFieldValue("posted", value);
                    }}
                    currency={formikProps.values.currency}
                    showCurrencySymbol={true}
                    fullWidth
                    minValue={0}
                    maxValue={999999999.99}
                  />
                ) : (
                  <Input
                    label={t("paymentForm.posted")}
                    value={formikProps.values.posted?.toFixed(2) || "0.00"}
                    disabled
                    fullWidth
                  />
                )}
              </Grid>
              <Grid item xs={12} md={6} id="cy-currencyFormContainer">
                {action === FormActionType.NEW ? (
                  <Autocomplete
                    id="cy-currency"
                    options={currencyOptions}
                    name="currency"
                    label={t("paymentForm.currency")}
                    value={formikProps.values.currency}
                    defaultValue={currencyOptions.find(
                      (option) => option.id === formikProps.values.currency
                    )}
                    onChange={(event, selectedOption) => {
                      formikProps.setFieldValue("currency", selectedOption?.id || CurrencyEnum.MXN);
                    }}
                   noOptionsText={t("paymentForm.noOptions")}
                  />
                ) : (
                  <Input
                    label={t("paymentForm.currency")}
                    value={getCurrencyLabel(formikProps.values.currency)}
                    disabled
                    fullWidth
                  />
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                {action === FormActionType.NEW ? (
                  <Autocomplete
                    options={paymentMethodOptions}
                    name="paymentMethod"
                    label={t("paymentForm.paymentMethod")}
                    value={formikProps.values.paymentMethod}
                    defaultValue={paymentMethodOptions.find(
                      (option) => option.id === formikProps.values.paymentMethod
                    )}
                    onChange={(event, selectedOption) => {
                      formikProps.setFieldValue(
                        "paymentMethod",
                        selectedOption?.id || PaymentMethodEnum.CASH
                      );
                    }}
                    noOptionsText={t("paymentForm.noOptions")}
                  />
                ) : (
                  <Input
                    label={t("paymentForm.paymentMethod")}
                    value={getPaymentMethodLabel(formikProps.values.paymentMethod)}
                    disabled
                    fullWidth
                  />
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                {action === FormActionType.NEW ? (
                  <Autocomplete
                    options={payerTypeOptions}
                    name="payerType"
                    label={t("paymentForm.payerType")}
                    value={formikProps.values.payerType}
                    defaultValue={payerTypeOptions.find(
                      (option) => option.id === formikProps.values.payerType
                    )}
                    onChange={(event, selectedOption) => {
                      formikProps.setFieldValue(
                        "payerType",
                        selectedOption?.id || PayerTypeEnum.PATIENT
                      );
                    }}
                    noOptionsText={t("paymentForm.noOptions")}
                  />
                ) : (
                  <Input
                    label={t("paymentForm.payerType")}
                    value={getPayerTypeLabel(formikProps.values.payerType)}
                    disabled
                    fullWidth
                  />
                )}
              </Grid>
              {action === FormActionType.MODIFY && (
                <Grid item xs={12} md={6}>
                  <Input
                    label={t("paymentForm.folio")}
                    value={formikProps.values.folio || ""}
                    disabled
                    fullWidth
                  />
                </Grid>
              )}
              {action === FormActionType.NEW && (
                <Grid item xs={12}>
                  <Card>
                    <Box p={2}>
                      <Typography variant="h6" mb={2}>
                        {t("paymentForm.financialSummary")} - {t("paymentForm.officialCurrency")}{" "}
                        (USD)
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                          <Input
                            label={t("paymentForm.totalDebt")}
                            value={formatCurrency(lastAmountDue, CurrencyEnum.USD)}
                            disabled
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Input
                            label={t("paymentForm.totalPaid")}
                            value={formatCurrency(
                              calculateTotalPaidWithCurrent(
                                formikProps.values.posted || 0,
                                formikProps.values.currency
                              ),
                              CurrencyEnum.USD
                            )}
                            disabled
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Box>
                            <Input
                              label={t("paymentForm.remainingBalance")}
                              value={formatCurrency(
                                calculateRemainingBalance(
                                  formikProps.values.posted || 0,
                                  formikProps.values.currency
                                ),
                                CurrencyEnum.USD
                              )}
                              disabled
                              fullWidth
                            />
                            {isRemainingBalanceNegative && (
                              <Typography
                                variant="caption"
                                color="error"
                                sx={{ mt: 0.5, display: "block" }}
                              >
                                {t("paymentForm.postedAmountGreaterThanDebt")}
                              </Typography>
                            )}
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>
                  </Card>
                </Grid>
              )}

              <Grid item xs={12}>
                <Card>
                  <Box p={2}>
                    <Stack direction="row" alignItems="center" gap={2} mb={2}>
                      <AttachFileIcon />
                      <Typography variant="h6">{t("paymentForm.paymentReceipts")}</Typography>
                      {paymentId && isPaymentAssetEndpointAvailable && (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => setShowUploadSection((prev) => !prev)}
                        >
                          {showUploadSection ? t("common.hide") : t("paymentForm.addReceipt")}
                        </Button>
                      )}
                    </Stack>
                    {assetError && (
                      <Box p={2} mb={2} bgcolor="#ffebee" border="1px solid #e57373">
                        <Stack direction="row" alignItems="center" gap={1}>
                          <WarningIcon color="error" />
                          <Typography variant="body2" color="error">
                            {assetError}
                          </Typography>
                        </Stack>
                      </Box>
                    )}
                    {paymentId != "" && isPaymentAssetEndpointAvailable && (
                      <Collapse in={showUploadSection}>
                        <Box mb={2}>
                          <Dropzone
                            openedCategory={MediaCategoryEnum.OTHER}
                            paymentId={paymentId}
                            setIsDataUpdated={handleDropzoneUpdate}
                          />
                        </Box>
                      </Collapse>
                    )}

                    {!isPaymentAssetEndpointAvailable && (
                      <Box p={2} bgcolor="#fff3cd" border="1px solid #ffeaa7">
                        <Stack direction="row" alignItems="center" gap={1}>
                          <WarningIcon color="warning" />
                          <Typography variant="h6">
                            {t("paymentForm.assetUploadNotAvailable")}
                          </Typography>
                        </Stack>
                      </Box>
                    )}

                    {!paymentId && action === FormActionType.NEW && (
                      <Box p={2} bgcolor="#f5f5f5">
                        <Typography variant="h6">
                          {t("paymentForm.createPaymentFirstMessage")}
                        </Typography>
                      </Box>
                    )}

                    {uploadedAssets.length > 0 && (
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                          <Typography variant="subtitle2" mb={1}>
                            {t("paymentForm.uploadedReceipts")} ({uploadedAssets.length})
                          </Typography>
                          <List>
                            {uploadedAssets.map((asset, index) => (
                              <ListItem
                                key={index}
                                onClick={() => handleFileClick(asset)}
                                sx={{
                                  cursor: "pointer",
                                  "&:hover": { backgroundColor: "#f0f0f0" },
                                  backgroundColor:
                                    selectedFile?.id === asset.id ? "#e3f2fd" : "transparent",
                                }}
                              >
                                <ListItemIcon>{getFileIcon(asset.mimetype || "")}</ListItemIcon>
                                <Typography variant="caption" sx={{ flex: 1 }}>
                                  {asset.orgName}
                                </Typography>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveAsset(asset.id);
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </ListItem>
                            ))}
                          </List>
                        </Grid>
                        <Grid item xs={12} md={8}>
                          {selectedFile ? (
                            <FilesViewer key={selectedFile.id} asset={selectedFile} />
                          ) : (
                            <Box
                              display="flex"
                              justifyContent="center"
                              alignItems="center"
                              minHeight={200}
                              bgcolor="#f5f5f5"
                            >
                              <Typography variant="h6">
                                {t("paymentForm.selectReceiptPreview")}
                              </Typography>
                            </Box>
                          )}
                        </Grid>
                      </Grid>
                    )}
                  </Box>
                </Card>
              </Grid>
            </Grid>
            <PermissionWrapper
              requiredPermissions={[PermissionsUser[EntityPermissions.PAYMENTS].EDIT]}
            >
              <Stack direction="row" spacing={2} mt={3} justifyContent="flex-end">
                {action === FormActionType.MODIFY ? (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={onCloseAction}
                    data-cy="closeButton"
                  >
                    {t("common.save")}
                  </Button>
                ) : action === FormActionType.NEW ? (
                  <Button
                    id="cy-payButton"
                    type="submit"
                    variant="contained"
                    color="success"
                    disabled={isLoading}
                    data-cy="payButton"
                  >
                    {isLoading ? t("common.processing") : t("paymentForm.pay")}
                  </Button>
                ) : null}
                <Button
                  variant="contained"
                  onClick={onCloseAction}
                  data-cy="cancelButton"
                  color="secondary"
                >
                  {action === FormActionType.NEW ? t("paymentForm.cancel") : t("common.close")}
                </Button>
              </Stack>
            </PermissionWrapper>
          </Form>
        );
      }}
    </FormikForm>
  );
};

export default PaymentForm;
